// Toast container — mounted once in Layout. Listens to the toast event bus
// and renders a stack of toasts that slide up (mobile bottom / desktop
// bottom-right). 3s auto-dismiss; multiple toasts stack.

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { subscribeToToasts, type ToastItem } from '../lib/toast';

const AUTO_DISMISS_MS = 3000;

export function ToastContainer() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => {
    return subscribeToToasts((t) => {
      setItems((prev) => [...prev, t]);
      window.setTimeout(() => {
        setItems((prev) => prev.filter((x) => x.id !== t.id));
      }, AUTO_DISMISS_MS);
    });
  }, []);

  return (
    <div
      className="fixed inset-x-0 bottom-24 sm:bottom-6 sm:left-auto sm:right-6 z-50 flex flex-col items-center sm:items-end gap-2 px-4 pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      <AnimatePresence initial={false}>
        {items.map((t) => (
          <motion.div
            key={t.id}
            layout
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className={
              'pointer-events-auto card border-l-4 shadow-lg max-w-sm w-full sm:w-auto ' +
              (t.variant === 'error'
                ? 'border-l-rose-500'
                : 'border-l-emerald-500')
            }
            role={t.variant === 'error' ? 'alert' : 'status'}
          >
            <div className="flex items-start gap-2">
              <span className="text-base shrink-0">
                {t.variant === 'error' ? '⚠️' : '✓'}
              </span>
              <p className="text-sm text-text/90 break-words">{t.message}</p>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
